import { ApiPagination, ProductFilters, ProductGrid } from "@/components";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Loader2, Package } from "lucide-react";
import { Product } from "@/types/AppTypes";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchAllCategoriesQueryOptions } from "@/queryOptions/categories/fetchPaginatedCategories";
import { fetchAllBrandsQueryOptions } from "@/queryOptions/brands/fetchPaginatedBrands";
import { getDefaultParseVariants } from "@/utils/helper/getDefaultParseVariants";
import { fetchCustomPaginatedProductsQueryOptions } from "@/queryOptions/products/fetchCustomPaginatedProducts";
import ScrollToTop from "@/components/ScrollToTop";
import { sortProductByName } from "@/utils/helper/sortProductByName";
import { useFilteredProductSearch } from "@/hooks/useFilteredProductSearch";
import { debounce } from "@/utils/debounce";

const Products: React.FC = () => {
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("all");
  const [category, setCategory] = useState("all");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const { data: brands, isLoading: brandsLoading } = useQuery(
    fetchAllBrandsQueryOptions(
      {},
      {
        enabled: true,
        staleTime: 1000 * 60 * 5,
      }
    )
  );

  const { data: categories, isLoading: categoriesLoading } = useQuery(
    fetchAllCategoriesQueryOptions(
      {},
      {
        enabled: true,
        staleTime: 1000 * 60 * 5,
      }
    )
  );

  const {
    data: products,
    isLoading: productsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    fetchCustomPaginatedProductsQueryOptions({}, 10, {
      staleTime: 1000 * 60 * 5,
      enabled: !isSearchActive,
    })
  );

  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    searchProducts,
    clearResults,
  } = useFilteredProductSearch({ debounceMs: 300 });

  const currentPage = products?.pages?.[currentPageIndex];
  const totalPages = products?.pages?.length || 0;

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleNext = async () => {
    const nextPageIndex = currentPageIndex + 1;

    if (nextPageIndex < totalPages) {
      setCurrentPageIndex(nextPageIndex);
    } else if (hasNextPage) {
      await fetchNextPage();
      setCurrentPageIndex(nextPageIndex);
    }
  };

  const productsForDisplay = useMemo(() => {
    if (isSearchActive) {
      return searchResults;
    }
    return sortProductByName(getDefaultParseVariants(currentPage?.items || []));
  }, [currentPage, searchResults, isSearchActive]);

  const categoriesWithAll = [{ name: "all", label: "All Categories" }, ...(categories || [])];
  const brandsWithAll = [{ name: "all", label: "All Brands" }, ...(brands || [])];

  const debouncedSearch = useRef(
    debounce(async (searchTerm: string, brandFilter: string, categoryFilter: string) => {
      const hasSearch = searchTerm.trim();
      const hasFilters = brandFilter !== "all" || categoryFilter !== "all";

      if (!hasSearch && !hasFilters) {
        setIsSearchActive(false);
        clearResults();
        return;
      }

      setIsSearchActive(true);

      const selectedBrand = brands?.find((b) => b.name === brandFilter);
      const selectedCategory = categories?.find((c) => c.name === categoryFilter);

      await searchProducts({
        search: searchTerm.trim(),
        brandId: selectedBrand?.id || brandFilter,
        categoryId: selectedCategory?.id || categoryFilter,
      });
    }, 300)
  );

  useEffect(() => {
    debouncedSearch.current(search, brand, category);
  }, [search, brand, category, brands, categories]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleClearFilters = () => {
    setSearch("");
    setBrand("all");
    setCategory("all");
  };

  const hasActiveFilters = search.trim() || brand !== "all" || category !== "all";
  const isLoading = productsLoading || searchLoading;
  const showNoResults = !isLoading && productsForDisplay.length === 0 && hasActiveFilters;
  const showResetButton = showNoResults && hasActiveFilters;

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-6">
          <Package className="h-8 w-8" />
          <h1 className="text-3xl font-bold">All Products</h1>
        </div>
        <ScrollToTop dependencies={[currentPageIndex]} />

        <div className="grid gap-6 md:grid-cols-[250px_1fr]">
          <div className="space-y-6">
            <ProductFilters
              categories={categoriesWithAll}
              brands={brandsWithAll}
              category={category}
              brand={brand}
              search={search}
              handleSearchChange={handleSearchChange}
              setCategory={setCategory}
              setBrand={setBrand}
              handleClearFilters={handleClearFilters}
              productCount={productsForDisplay.length}
              totalProducts={productsForDisplay.length}
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">{searchLoading ? "Searching products..." : "Loading..."}</span>
            </div>
          ) : (
            <div>
              {searchError && (
                <div className="text-red-500 text-center py-4">Error: {searchError}</div>
              )}

              {productsForDisplay.length > 0 ? (
                <ProductGrid
                  gridItems={productsForDisplay}
                  className="grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2"
                />
              ) : showNoResults ? (
                <div className="flex flex-col justify-center items-center h-64 text-gray-500">
                  <Package className="h-16 w-16 mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No products found</h3>
                  <p className="text-sm text-center">
                    Try adjusting your search terms or filters to find more products.
                  </p>
                  {showResetButton && (
                    <button
                      onClick={handleClearFilters}
                      className="mt-4 px-4 py-2 bg-black text-white hover:bg-black/80 transition-colors rounded-md"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {!isLoading && !hasActiveFilters && (
          <div className="flex justify-end items-center mt-4">
            <ApiPagination
              currentPageIndex={currentPageIndex}
              totalPages={products?.pages?.length || 0}
              hasNextPage={currentPageIndex < (products?.pages?.length || 0) - 1 || hasNextPage}
              isFetching={isFetchingNextPage}
              onPrevious={handlePrevious}
              onNext={handleNext}
              variant="minimal"
            />
          </div>
        )}

        {!isLoading && productsForDisplay.length > 0 && (
          <div className="text-center mt-4 text-sm text-gray-600">
            {isSearchActive
              ? `Found ${productsForDisplay.length} product${
                  productsForDisplay.length === 1 ? "" : "s"
                }`
              : `Showing ${productsForDisplay.length} product${
                  productsForDisplay.length === 1 ? "" : "s"
                }`}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
